import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context";
import useFetchObjects from "../../../api/useFetchObjects";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = ["#1976d2", "#2e7d32", "#ed6c02", "#d32f2f", "#9c27b0"];

const AuditIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [timeRange, setTimeRange] = useState("all");
  const [fetchParams, setFetchParams] = useState("");

  useEffect(() => {
    let params = "?page_size=1000"; // Get more data for stats
    if (timeRange !== "all") {
      const now = new Date();
      let startDate = new Date();
      switch (timeRange) {
        case "today":
          startDate.setHours(0, 0, 0, 0);
          break;
        case "week":
          startDate.setDate(now.getDate() - 7);
          startDate.setHours(0, 0, 0, 0);
          break;
        case "month":
          startDate.setMonth(now.getMonth() - 1);
          startDate.setHours(0, 0, 0, 0);
          break;
        case "year":
          startDate.setFullYear(now.getFullYear() - 1);
          startDate.setHours(0, 0, 0, 0);
          break;
        default:
          break;
      }
      if (timeRange !== "all") {
        params += `&created_at__gte=${startDate.toISOString()}`;
      }
    }
    setFetchParams(params);
  }, [timeRange]);

  const { data: logsData, isLoading: logsLoading } = useFetchObjects(
    ["audit-logs-stats", fetchParams],
    `audit-logs/${fetchParams}`,
    token,
    { enabled: !!token }
  );

  const { data: requestsData, isLoading: requestsLoading } = useFetchObjects(
    ["audit-requests-stats", fetchParams],
    `audit-requests/${fetchParams}`,
    token,
    { enabled: !!token }
  );

  // Calculate statistics
  const logs = Array.isArray(logsData) ? logsData : logsData?.results || [];
  const requests = Array.isArray(requestsData)
    ? requestsData
    : requestsData?.results || [];

  // Action type breakdown
  const actionBreakdown = logs.reduce((acc, log) => {
    const action = log.action || "unknown";
    acc[action] = (acc[action] || 0) + 1;
    return acc;
  }, {});

  const actionChartData = Object.entries(actionBreakdown).map(([action, count]) => ({
    name: t(`audit.action.${action}`) || action,
    value: count,
  }));

  // User activity (top 5 users)
  const userActivity = logs.reduce((acc, log) => {
    const user = log.actor_username || "Unknown";
    acc[user] = (acc[user] || 0) + 1;
    return acc;
  }, {});

  const userChartData = Object.entries(userActivity)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([user, count]) => ({
      name: user,
      value: count,
    }));

  // Daily activity chart
  const dailyActivity = logs.reduce((acc, log) => {
    if (log.created_at) {
      const date = new Date(log.created_at).toLocaleDateString();
      acc[date] = (acc[date] || 0) + 1;
    }
    return acc;
  }, {});

  const dailyChartData = Object.entries(dailyActivity)
    .sort((a, b) => new Date(a[0]) - new Date(b[0]))
    .slice(-7)
    .map(([date, count]) => ({
      date: new Date(date).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      }),
      count,
    }));

  // HTTP method breakdown
  const methodBreakdown = requests.reduce((acc, req) => {
    const method = req.method || "Unknown";
    acc[method] = (acc[method] || 0) + 1;
    return acc;
  }, {});

  const methodChartData = Object.entries(methodBreakdown).map(([method, count]) => ({
    name: method,
    value: count,
  }));

  const stats = [
    {
      title: t("audit.totalLogs"),
      value: logsData?.count || logs.length,
      color: "#1976d2",
      icon: "📊",
    },
    {
      title: t("audit.totalRequests"),
      value: requestsData?.count || requests.length,
      color: "#2e7d32",
      icon: "🌐",
    },
    {
      title: t("audit.activeUsers"),
      value: Object.keys(userActivity).length,
      color: "#ed6c02",
      icon: "👥",
    },
    {
      title: t("audit.modelsTracked"),
      value: new Set(logs.map((log) => `${log.app_label}.${log.model}`)).size,
      color: "#9c27b0",
      icon: "📦",
    },
  ];

  if (logsLoading || requestsLoading) {
    return (
      <Grid container justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid container maxWidth="xl" mb={3} spacing={2}>
      {/* Time Range Filter */}
      <Grid item xs={12}>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t("audit.timeRange")}</InputLabel>
            <Select
              value={timeRange}
              label={t("audit.timeRange")}
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="all">{t("audit.allTime")}</MenuItem>
              <MenuItem value="today">{t("audit.today")}</MenuItem>
              <MenuItem value="week">{t("audit.lastWeek")}</MenuItem>
              <MenuItem value="month">{t("audit.lastMonth")}</MenuItem>
              <MenuItem value="year">{t("audit.lastYear")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Grid>

      {/* Statistics Cards */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: "100%",
                boxShadow: 2,
                transition: "transform 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
              }}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "120px",
                  }}
                >
                  <Typography variant="h3" sx={{ mb: 1 }}>
                    {stat.icon}
                  </Typography>
                  <Typography
                    variant="h4"
                    component="div"
                    sx={{
                      fontWeight: "bold",
                      color: stat.color,
                      mb: 1,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    align="center"
                  >
                    {stat.title}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts */}
      <Grid container spacing={2}>
        {/* Action Type Breakdown */}
        {actionChartData.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("audit.actionBreakdown")}
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={actionChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {actionChartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Top Users */}
        {userChartData.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("audit.topUsers")}
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={userChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="value" fill="#1976d2" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Daily Activity */}
        {dailyChartData.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("audit.dailyActivity")}
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={dailyChartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#2e7d32" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* HTTP Methods */}
        {methodChartData.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t("audit.httpMethods")}
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={methodChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) =>
                        `${name} ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {methodChartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

export default AuditIndex;
